import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateBusiness } from '../../../api/routes/businesses'
import { useGetAllBusinesses } from '../../../api/routes/businesses'
import { Button } from '../../../components/ui/button'
import { toast } from 'react-toastify'
import { ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'

const initialForm = {
    name: '',
    address: '',
    parentBusinessId: '',
}

const RegisterBusinessRoute = () => {
    const [form, setForm] = useState(initialForm)
    const navigate = useNavigate()
    const createBusiness = useCreateBusiness()
    const { data: businesses } = useGetAllBusinesses()

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        if (!form.name.trim()) {
            toast.error('Business name is required')
            return
        }

        createBusiness.mutate(
            {
                name: form.name.trim(),
                address: form.address.trim() || undefined,
                parentBusinessId: form.parentBusinessId || null,
            },
            {
                onSuccess: () => {
                    toast.success('Business registered successfully')
                    navigate('/businesses')
                },
                onError: (error: any) => {
                    toast.error(
                        error?.response?.data?.message ||
                            error.message ||
                            'Failed to register business'
                    )
                },
            }
        )
    }

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <div>
                <div className="mb-6">
                    <Link
                        to="/businesses"
                        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Businesses
                    </Link>
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                        Register Business
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Create a new business in the system.
                    </p>
                </div>

                <div className="rounded-lg border bg-card p-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Business Name
                            </label>
                            <input
                                value={form.name}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        name: e.target.value,
                                    }))
                                }
                                required
                                placeholder="Enter business name"
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Address
                            </label>
                            <input
                                value={form.address}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        address: e.target.value,
                                    }))
                                }
                                placeholder="Enter business address"
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Parent Business
                            </label>
                            <select
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                value={form.parentBusinessId}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        parentBusinessId: e.target.value,
                                    }))
                                }
                            >
                                <option value="">None (Top-level)</option>
                                {businesses?.map((business: any) => (
                                    <option
                                        key={business.id}
                                        value={business.id}
                                    >
                                        {business.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/businesses')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={createBusiness.isPending}
                            >
                                {createBusiness.isPending
                                    ? 'Registering...'
                                    : 'Register Business'}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export { RegisterBusinessRoute }
